if (!customElements.get('c-product-slider')) {
  const TABLET_BREAKPOINT = 989;
  const MOBILE_BREAKPOINT = 749;
  const CARD_WIDTH_TABLET = '35vw';
  const CARD_WIDTH_MOBILE = '60.6vw';
  const GAP_DESKTOP = '4rem';
  const GAP_MOBILE = '1.2rem';
  const PADDING_TABLET = '5rem';
  const PADDING_MOBILE = '1.5rem';
  const SLIDE_SPEED = 600;
  const DEFAULT_COLUMNS = 4;
  const TOUCH_PER_PAGE = 1;
  const DRAG_MIN_THRESHOLD = 10;
  const SPLIDE_SCRIPT_SELECTOR = 'script[src*="@splidejs/splide@4.1.4"]';
  /** Splideで商品カードスライダーを管理するカスタム要素。 */
  class CProductSlider extends HTMLElement {
    /** カスタム要素の接続時にSplideを初期化する。 */
    connectedCallback() {
      this.slider = this.querySelector('.splide');
      if (!this.slider) return;

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      if (window.Splide) {
        this.mount();
        return;
      }

      const mountWhenConnected = () => {
        if (this.isConnected) this.mount();
      };

      if (document.readyState === 'complete') {
        const splideScript = document.querySelector(SPLIDE_SCRIPT_SELECTOR);
        splideScript?.addEventListener('load', mountWhenConnected, { once: true, signal });
        return;
      }

      window.addEventListener('load', mountWhenConnected, { once: true, signal });
    }

    /** Splideと独自矢印を初期化する。 */
    mount() {
      if (this.splide || !window.Splide) return;

      const parsedColumns = Number.parseInt(this.dataset.columns, 10);
      const columns = Number.isFinite(parsedColumns) && parsedColumns > 0 ? parsedColumns : DEFAULT_COLUMNS;
      const slidesCount = this.querySelectorAll('.c_product-slider__slide').length;
      const signal = this.abortController?.signal;

      try {
        this.splide = new window.Splide(this.slider, {
          type: 'slide',
          perPage: columns,
          gap: GAP_DESKTOP,
          arrows: false,
          pagination: false,
          trimSpace: true,
          speed: SLIDE_SPEED,
          keyboard: 'focused',
          ...(window.cSliderStrings ? { i18n: window.cSliderStrings } : {}),
          drag: slidesCount > columns,
          dragMinThreshold: { mouse: DRAG_MIN_THRESHOLD, touch: DRAG_MIN_THRESHOLD },
          breakpoints: {
            [TABLET_BREAKPOINT]: {
              perPage: TOUCH_PER_PAGE,
              fixedWidth: CARD_WIDTH_TABLET,
              gap: GAP_MOBILE,
              padding: { left: PADDING_TABLET, right: PADDING_TABLET },
              drag: 'free',
              snap: false,
            },
            [MOBILE_BREAKPOINT]: {
              perPage: TOUCH_PER_PAGE,
              fixedWidth: CARD_WIDTH_MOBILE,
              gap: GAP_MOBILE,
              padding: { left: PADDING_MOBILE, right: PADDING_MOBILE },
              drag: 'free',
              snap: false,
            },
          },
        });

        this.previousArrow = this.querySelector('.c_product-slider__arrow--prev');
        this.nextArrow = this.querySelector('.c_product-slider__arrow--next');
        this.previousArrow?.addEventListener('click', () => this.splide?.go('<'), { signal });
        this.nextArrow?.addEventListener('click', () => this.splide?.go('>'), { signal });

        const updateSliderState = () => {
          this.updateArrowState();
          this.updateArrowPosition();
        };

        this.splide.on('mounted move moved resized updated refresh', updateSliderState);
        this.splide.mount();
        updateSliderState();
      } catch (error) {
        console.error('Failed to initialize product slider.', error);
        this.splide = null;
      }
    }

    /** 現在位置に応じて矢印の無効状態を更新する。 */
    updateArrowState() {
      if (!this.splide || !this.previousArrow || !this.nextArrow) return;

      const { Controller } = this.splide.Components;
      this.previousArrow.disabled = Controller.getPrev() < 0;
      this.nextArrow.disabled = Controller.getNext() < 0;
    }

    /** 最初のカード画像に矢印の縦位置を合わせる。 */
    updateArrowPosition() {
      const firstSlide = this.querySelector('.c_product-slider__slide');
      const cardMedia = firstSlide?.querySelector('.card__media') ?? firstSlide?.querySelector('.card__inner');
      if (!cardMedia) return;

      this.style.setProperty('--c-product-slider-arrow-top', `${cardMedia.offsetHeight / 2}px`);
    }

    /** カスタム要素の切断時にSplideを破棄する。 */
    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;

      if (this.splide) {
        this.splide.destroy();
        this.splide = null;
      }

      this.slider?.classList.remove('is-initialized', 'is-active');
    }
  }

  customElements.define('c-product-slider', CProductSlider);
}
